// Import necessary dependencies for React hooks, routing, and analytics
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useSession } from '@/context/SessionContext';
import gTagConstants from '@/constants/gEvenets.constants';

// Define standard scroll depth thresholds for Google Analytics 4
// These percentages represent how far down the page the user has scrolled
const SCROLL_THRESHOLDS = [10, 25, 50, 75, 90];

// Define the structure of the scroll event data that will be sent to GA4
interface ScrollEventParams {
  batch_ordering_id: number; // Unique identifier for event ordering
  batch_page_id: number; // Unique identifier for the page load
  ga_session_number: string; // GA session number from cookie
  ga_session_id: string; // GA session ID from cookie
  page_location: string; // Current page URL
  page_referrer: string; // Previous page URL
  page_title: string; // Page title
  engagement_time_sec: number; // Time spent on page in seconds
  device_category: string; // Device type (mobile/tablet/desktop)
  session_id: string; // User session identifier
  sku?: string; // Optional product SKU for product pages
  page_type: string; // Type of page (e.g., product, category)
  entrances: number; // Always 0 for scroll events
  percent_scrolled: number; // How far down the page the user has scrolled
}

/**
 * Component that tracks and reports scroll depth events to Google Analytics 4.
 * Fires events when user scrolls past predefined thresholds (10%, 25%, 50%, 75%, 90%).
 * Also tracks engagement time spent on the page.
 */
export default function ScrollEvent() {
  // Get router and session context
  const router = useRouter();
  const session = useSession();

  // Get analytics helper functions and parameters
  const {
    pageParams,
    getDeviceCategory,
    getNextBatchOrderingId,
    getBatchPageId,
    getGASessionId,
    getGASessionNumber,
    skuSlug,
    getPageType,
  } = useAnalytics();

  // Refs to track state that persists between renders
  const engagementTimeRef = useRef(0); // Tracks time spent on page
  const timerRef = useRef<NodeJS.Timeout | null>(null); // Stores interval timer
  const firedThresholdsRef = useRef<Set<number>>(new Set()); // Tracks which scroll thresholds have been triggered

  /**
   * Sends a scroll event to Google Analytics with current page metrics
   * @param percent_scrolled - The scroll depth percentage that triggered this event
   */
  const fireScrollEvent = (percent_scrolled: number) => {
    const sessionId = session.sessionToken || '';
    const entrances = 0; // Scroll events are never page entrances

    // Prepare event parameters according to GA4 requirements
    const params: ScrollEventParams = {
      batch_ordering_id: getNextBatchOrderingId(),
      batch_page_id: getBatchPageId(),
      ga_session_number: getGASessionNumber(),
      ga_session_id: getGASessionId(),
      page_location: pageParams.page_location,
      page_referrer: pageParams.page_referrer,
      page_title: pageParams.page_title,
      engagement_time_sec: engagementTimeRef.current,
      device_category: getDeviceCategory(),
      session_id: sessionId,
      sku: skuSlug || undefined,
      page_type: getPageType(),
      entrances,
      percent_scrolled,
    };

    // Send event to GA4 using either gtag or dataLayer
    if (typeof window !== 'undefined') {
      if (window.gtag) {
        window.gtag('event', gTagConstants.eventKeys.scroll, params);
      } else if (window.dataLayer) {
        window.dataLayer.push({ event: gTagConstants.eventKeys.scroll, ...params });
      }
    }
  };

  // Set up engagement timer to track time spent on page
  useEffect(() => {
    // Increment engagement time every second
    timerRef.current = setInterval(() => {
      engagementTimeRef.current += 1;
    }, 1000);

    // Cleanup: clear interval when component unmounts
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // Set up scroll tracking
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Reset tracked thresholds when route changes
    firedThresholdsRef.current = new Set();

    /**
     * Handles scroll events and fires GA4 events when thresholds are crossed
     */
    const handleScroll = () => {
      // Calculate current scroll position and total scrollable height
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;

      // Skip if page height is 0 or negative
      if (docHeight <= 0) return;

      // Calculate scroll percentage
      const percent = Math.round((scrollTop / docHeight) * 100);

      // Check each threshold and fire event if newly crossed
      for (const threshold of SCROLL_THRESHOLDS) {
        if (percent >= threshold && !firedThresholdsRef.current.has(threshold)) {
          firedThresholdsRef.current.add(threshold);
          fireScrollEvent(threshold);
        }
      }
    };

    // Add scroll listener with passive flag for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup: remove scroll listener when component unmounts
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageParams.page_location, router.pathname]);

  // This component doesn't render anything
  return null;
}
